import ClientCMS from './ClientCMS'
import type {
  Builder,
  ContentData,
  ContentPage,
  ContentTypeOptions,
  GetCMSPageParams,
} from './ClientCMS'
import config from '../../store.config'

type GetCMSPageDataByContentTypeProps = {
  contentType: string
  params?: Partial<ContentTypeOptions>
  builder?: Builder
  pageId?: string
  versionId?: string
}

const clientCMS = (builder: string) =>
  new ClientCMS({
    builder,
    workspace: config.api.workspace,
    tenant: config.api.storeId,
  })

export const getCMSPageDataByContentType = async ({
  contentType,
  params,
  builder = 'faststore',
  versionId,
  pageId,
}: GetCMSPageDataByContentTypeProps): Promise<ContentData> => {
  const data = await clientCMS(builder).getCMSPagesByContentType(contentType, {
    ...params,
    versionId,
    pageId,
  })

  const cms =
    'sections' in data ? (data as unknown as ContentData) : data?.data?.[0]

  return cms ?? null
}

export const getCMSPageData = async ({
  contentType,
  documentId,
  versionId,
  builder = 'faststore',
}: GetCMSPageParams): Promise<ContentData> => {
  return clientCMS(builder).getCMSPage({
    contentType,
    documentId,
    versionId,
  })
}

type GetCMSPageListProps = {
  contentType: string
  params?: Partial<ContentTypeOptions>
  builder?: Builder
}

/**
 * Returns the full paginated ContentPage (data[], hasNextPage, totalItems).
 * Use this for listing endpoints that need all items, not just the first one.
 */
export const getCMSPageList = async ({
  contentType,
  params,
  builder = 'faststore',
}: GetCMSPageListProps): Promise<ContentPage> => {
  return clientCMS(builder).getCMSPagesByContentType(contentType, params)
}

/**
 * Returns the ids of every content type currently published for a builder.
 * Used to validate dynamic slugs (e.g. blog categories) against what is
 * actually registered in the CMS, instead of a hardcoded list.
 */
export const getCMSContentTypeIds = async (
  builder: Builder = 'faststore'
): Promise<string[]> => {
  return clientCMS(builder).getAllContentTypeIds()
}
